/**
 * Fonte única de labels PT-BR para valores internos (ids/enums) exibidos na UI.
 * Nenhum componente deve renderizar valores crus como `tech_reviewer`,
 * `improvement` ou `ready` — sempre passar por estes mapas.
 */

export const CATEGORY_LABELS: Record<string, string> = {
  general: 'Geral',
  feature: 'Feature',
  bug: 'Bug',
  improvement: 'Melhoria',
  design: 'Design',
}

export const IDEA_STATUS_LABELS: Record<string, string> = {
  open: 'Rascunho',
  ready: 'Pronta',
  processing: 'Processando',
  done: 'Incluída',
  error: 'Erro',
  archived: 'Arquivada',
  suggested: 'Sugerida',
}

/** Labels dos skills do pipeline (chips do AgentTeamPanel, badges de peers). */
export const SKILL_LABELS: Record<string, string> = {
  ingest: 'Ingestão',
  synthesis: 'Síntese do SDD',
  tech_reviewer: 'Revisão técnica',
  risk_reviewer: 'Análise de riscos',
  innovation_reviewer: 'Inovação',
  sustainability_reviewer: 'Sustentabilidade',
  event_storm: 'Event Storming',
  diagrams: 'Diagramas',
  prototype: 'Protótipo',
  studio: 'Studio',
}

export const FEASIBILITY_LABELS: Record<string, string> = {
  viavel: 'Viável',
  parcialmente_viavel: 'Parcial',
  inviavel: 'Inviável',
  desconhecido: 'Indefinido',
}

export function categoryLabel(value: string | undefined | null): string {
  if (!value) return CATEGORY_LABELS.general
  return CATEGORY_LABELS[value] ?? value
}

export function ideaStatusLabel(value: string | undefined | null): string {
  if (!value) return IDEA_STATUS_LABELS.open
  return IDEA_STATUS_LABELS[value] ?? value
}

export function skillLabel(value: string | undefined | null): string {
  if (!value) return ''
  return SKILL_LABELS[value] ?? value
}

export function feasibilityLabel(value: string | undefined | null): string {
  if (!value) return FEASIBILITY_LABELS.desconhecido
  return FEASIBILITY_LABELS[value] ?? value
}

/** Formas do Studio (barra de ferramentas e painel de estilo). */
export const SHAPE_LABELS: Record<string, string> = {
  rect: 'Retângulo',
  rounded: 'Retângulo arredondado',
  ellipse: 'Elipse',
  diamond: 'Losango',
  cylinder: 'Banco de dados',
  actor: 'Pessoa',
  cloud: 'Nuvem',
  document: 'Documento',
  queue: 'Fila',
  text: 'Texto',
  note: 'Nota',
  image: 'Imagem',
  group: 'Grupo',
  icon: 'Ícone',
  table: 'Tabela / entidade',
}

/** Semântica de arquitetura de um elemento do Studio. */
export const NODE_KIND_LABELS: Record<string, string> = {
  service: 'Serviço',
  database: 'Banco de dados',
  queue: 'Fila / tópico',
  cache: 'Cache',
  actor: 'Pessoa / papel',
  external_system: 'Sistema externo',
  frontend: 'Frontend',
  storage: 'Armazenamento',
  boundary: 'Fronteira',
  lane: 'Raia',
  other: 'Outro',
}

export const EDGE_ROUTING_LABELS: Record<string, string> = {
  orthogonal: 'Ortogonal',
  straight: 'Reta',
  curved: 'Curva',
}

export const ROOM_TYPE_LABELS: Record<string, string> = {
  whiteboard: 'Ideias',
  studio: 'Studio',
}

export function shapeLabel(value: string | undefined | null): string {
  if (!value) return ''
  return SHAPE_LABELS[value] ?? value
}

export function nodeKindLabel(value: string | undefined | null): string {
  if (!value) return ''
  return NODE_KIND_LABELS[value] ?? value
}

/** Linha de tecnologia no estilo C4: "[Serviço: Java 21 / Spring Boot]". */
export function nodeTechLine(kind: string | undefined | null, tech: string | undefined | null): string {
  const t = (tech || '').trim()
  const k = kind && kind !== 'other' && kind !== 'lane' ? nodeKindLabel(kind) : ''
  if (t && k) return `[${k}: ${t}]`
  if (t) return `[${t}]`
  return ''
}

/** Categorias da biblioteca de ícones do Studio. */
export const ICON_CATEGORY_LABELS: Record<string, string> = {
  aws: 'AWS',
  gcp: 'Google Cloud',
  azure: 'Azure',
  plataforma: 'Plataforma e DevOps',
  dados: 'Dados',
  mensageria: 'Mensageria e APIs',
  observabilidade: 'Observabilidade',
  seguranca: 'Segurança',
  apps: 'Apps e linguagens',
  saas: 'SaaS e integrações',
  ia: 'IA',
  genericos: 'Genéricos',
}
